import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import {
  SEARCH_FIELD_STYLES,
  searchStartAdornment,
  searchClearAdornment,
} from '../templates/SearchField';

// Product search hits the API — longer debounce than local task filter (250ms)
// to avoid spamming the backend as the user types a multi-word query.
const PRODUCT_SEARCH_DEBOUNCE_MS = 700;
const MIN_SEARCH_LENGTH = 3;

const styles = {
  container: { width: '100%' },
  input: {
    ...SEARCH_FIELD_STYLES.input,
    '& .MuiOutlinedInput-root': {
      ...SEARCH_FIELD_STYLES.input['& .MuiOutlinedInput-root'],
      bgcolor: 'background.paper',
    },
    '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
      paddingLeft: '14px !important',
    },
  },
};

const ProductsSearch = forwardRef(({ searchProduct, resetSearch, onClear, options = [], value, onValueChange }, ref) => {
  const { t } = useTranslation();

  const debouncedSearch = useDebouncedCallback((value) => {
    if (value.length === 0) {
      resetSearch();
    } else if (value.length >= MIN_SEARCH_LENGTH) {
      searchProduct('substring', value);
    }
  }, PRODUCT_SEARCH_DEBOUNCE_MS);

  useImperativeHandle(ref, () => ({
    cancel: () => {
      debouncedSearch.cancel();
    },
  }));

  const handleInputChange = useCallback(
    (_, next) => {
      onValueChange(next);
      debouncedSearch(next);
    },
    [onValueChange, debouncedSearch],
  );

  const handleSelect = useCallback(
    (_, selected) => {
      if (!selected) return;
      const val = typeof selected === 'string' ? selected : selected.description;
      onValueChange(val);
      // User explicitly picked an option — skip debounce, fire now.
      debouncedSearch.cancel();
      if (val.length >= MIN_SEARCH_LENGTH) {
        searchProduct('substring', val);
      }
    },
    [onValueChange, searchProduct, debouncedSearch],
  );

  const handleClear = useCallback(() => {
    debouncedSearch.cancel();
    onValueChange('');
    resetSearch();
    if (onClear) onClear();
  }, [onValueChange, debouncedSearch, resetSearch, onClear]);

  return (
    <Box sx={styles.container}>
      <Autocomplete
        freeSolo
        options={options}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : (option.description || '')
        }
        getOptionKey={(option) =>
          typeof option === 'string' ? option : `${option.item}-${option.ean}`
        }
        inputValue={value}
        onInputChange={handleInputChange}
        onChange={handleSelect}
        filterOptions={(x) => x}
        noOptionsText={t('bruno.products.no_options', 'No products found')}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            placeholder={t('bruno.products.search_placeholder', 'Search by ID, name or barcode...')}
            size="small"
            sx={styles.input}
            InputProps={{
              ...params.InputProps,
              startAdornment: searchStartAdornment,
              endAdornment: value ? searchClearAdornment(handleClear) : null,
            }}
          />
        )}
      />
    </Box>
  );
});

export default ProductsSearch;
